import React, {
    useCallback,
    useEffect,
    useState,
} from "react";

import {
    FaCheckCircle,
    FaGraduationCap,
    FaMedal,
    FaRedo,
    FaSearch,
    FaTrophy,
    FaUsers,
} from "react-icons/fa";

import api from "../api/api";

const BRAND = {
    navy: "#062A4B",
    blue: "#063A6B",
    gold: "#F6C944",
    green: "#4FAE22",
    red: "#C62828",
    gray: "#64748B",
    light: "#F4F7FA",
    border: "#DCE3EA",
    white: "#FFFFFF",
};

const EntrancePublicResults = () => {
    const [data, setData] = useState({
        published: false,
        total_took: 0,
        total_passed: 0,
        total_failed: 0,
        results: [],
    });

    const [loading, setLoading] =
        useState(true);

    const [error, setError] =
        useState("");

    const [search, setSearch] =
        useState("");

    const loadResults = useCallback(
        async () => {
            try {
                setError("");

                const response =
                    await api.get(
                        "/api/entrance_examination/public/results/"
                    );

                setData(
                    response?.data || {
                        published: false,
                        total_took: 0,
                        total_passed: 0,
                        total_failed: 0,
                        results: [],
                    }
                );
            } catch (requestError) {
                console.error(
                    "Public entrance results loading failed:",
                    requestError
                );

                setError(
                    requestError?.response?.data
                        ?.detail ||
                        "Unable to load entrance examination results."
                );
            } finally {
                setLoading(false);
            }
        },
        []
    );

    useEffect(() => {
        loadResults();

        /*
         * Keep an already-open public results
         * page updated when the administrator
         * publishes or unpublishes results.
         */
        const interval =
            window.setInterval(
                loadResults,
                5000
            );

        return () =>
            window.clearInterval(
                interval
            );
    }, [loadResults]);

    const results =
        Array.isArray(data?.results)
            ? data.results
            : [];

    const filteredResults =
        results.filter((item) => {
            const searchValue =
                search
                    .trim()
                    .toLowerCase();

            if (!searchValue) {
                return true;
            }

            return (
                String(
                    item?.applicant_name ||
                        ""
                )
                    .toLowerCase()
                    .includes(
                        searchValue
                    ) ||
                String(
                    item?.application_number ||
                        ""
                )
                    .toLowerCase()
                    .includes(
                        searchValue
                    )
            );
        });

    if (loading) {
        return (
            <div
                style={{
                    minHeight: "100vh",
                    display: "flex",
                    alignItems:
                        "center",
                    justifyContent:
                        "center",
                    background:
                        BRAND.light,
                }}
            >
                <div
                    style={{
                        textAlign:
                            "center",
                        color:
                            BRAND.navy,
                    }}
                >
                    <div
                        style={{
                            width: 42,
                            height: 42,
                            border: `4px solid ${BRAND.border}`,
                            borderTopColor:
                                BRAND.gold,
                            borderRadius:
                                "50%",
                            margin:
                                "0 auto 14px",
                            animation:
                                "entrancePublicSpin .8s linear infinite",
                        }}
                    />

                    <div
                        style={{
                            fontWeight: 700,
                        }}
                    >
                        Loading Entrance
                        Results...
                    </div>
                </div>

                <style>
                    {`
                        @keyframes entrancePublicSpin {
                            to {
                                transform: rotate(360deg);
                            }
                        }
                    `}
                </style>
            </div>
        );
    }

    if (error) {
        return (
            <div
                style={{
                    minHeight: "100vh",
                    background:
                        BRAND.light,
                    padding: 20,
                }}
            >
                <div
                    style={{
                        maxWidth: 700,
                        margin:
                            "100px auto",
                        background:
                            BRAND.white,
                        border:
                            `1px solid ${BRAND.border}`,
                        borderRadius: 14,
                        padding: 35,
                        textAlign:
                            "center",
                    }}
                >
                    <div
                        style={{
                            color:
                                BRAND.red,
                            fontSize: 18,
                            fontWeight: 800,
                            marginBottom: 10,
                        }}
                    >
                        Unable to Load
                        Results
                    </div>

                    <div
                        style={{
                            color:
                                BRAND.gray,
                            marginBottom: 20,
                        }}
                    >
                        {error}
                    </div>

                    <button
                        onClick={
                            loadResults
                        }
                        style={{
                            border: "none",
                            background:
                                BRAND.navy,
                            color:
                                BRAND.white,
                            borderRadius: 8,
                            padding:
                                "10px 17px",
                            cursor:
                                "pointer",
                            fontWeight: 700,
                        }}
                    >
                        <FaRedo
                            style={{
                                marginRight: 7,
                            }}
                        />
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    if (!data?.published) {
        return (
            <div
                style={{
                    minHeight: "100vh",
                    background:
                        BRAND.light,
                    padding: 20,
                }}
            >
                <style>
                    {`
                        .public-result-empty {
                            animation:
                                publicResultFade
                                .5s ease;
                        }

                        @keyframes publicResultFade {
                            from {
                                opacity: 0;
                                transform:
                                    translateY(10px);
                            }

                            to {
                                opacity: 1;
                                transform:
                                    translateY(0);
                            }
                        }
                    `}
                </style>

                <div
                    className="public-result-empty"
                    style={{
                        maxWidth: 750,
                        margin:
                            "90px auto",
                        background:
                            BRAND.white,
                        border:
                            `1px solid ${BRAND.border}`,
                        borderRadius: 16,
                        padding:
                            "45px 30px",
                        textAlign:
                            "center",
                        boxShadow:
                            "0 8px 30px rgba(6,42,75,.07)",
                    }}
                >
                    <div
                        style={{
                            width: 76,
                            height: 76,
                            margin:
                                "0 auto 18px",
                            borderRadius:
                                "50%",
                            background:
                                "#EEF3F7",
                            display:
                                "flex",
                            alignItems:
                                "center",
                            justifyContent:
                                "center",
                            color:
                                BRAND.navy,
                            fontSize: 31,
                        }}
                    >
                        <FaGraduationCap />
                    </div>

                    <h1
                        style={{
                            margin: 0,
                            color:
                                BRAND.navy,
                            fontSize: 28,
                        }}
                    >
                        Entrance Examination
                        Results
                    </h1>

                    <p
                        style={{
                            color:
                                BRAND.gray,
                            margin:
                                "12px auto 0",
                            maxWidth: 560,
                            lineHeight:
                                1.6,
                        }}
                    >
                        The entrance examination
                        results have not been
                        published yet. Please
                        check back later.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div
            style={{
                minHeight: "100vh",
                background:
                    BRAND.light,
                padding:
                    "25px 20px 45px",
            }}
        >
            <style>
                {`
                    .public-results-container {
                        max-width: 1250px;
                        margin: 0 auto;
                    }

                    .public-results-hero {
                        background:
                            linear-gradient(
                                135deg,
                                #062A4B 0%,
                                #063A6B 100%
                            );
                        color: white;
                        border-radius: 16px;
                        padding: 28px;
                        box-shadow:
                            0 8px 28px
                            rgba(6,42,75,.12);
                    }

                    .public-results-stats {
                        display: grid;
                        grid-template-columns:
                            repeat(3, 1fr);
                        gap: 14px;
                        margin-top: 18px;
                    }

                    .public-results-stat {
                        background: white;
                        border: 1px solid ${BRAND.border};
                        border-radius: 12px;
                        padding: 18px;
                        box-shadow:
                            0 4px 16px
                            rgba(6,42,75,.05);
                    }

                    .public-results-stat-number {
                        font-size: 27px;
                        font-weight: 800;
                        color: ${BRAND.navy};
                        margin-top: 7px;
                    }

                    .public-results-table-wrap {
                        background: white;
                        border:
                            1px solid ${BRAND.border};
                        border-radius: 14px;
                        overflow: hidden;
                        margin-top: 18px;
                        box-shadow:
                            0 5px 20px
                            rgba(6,42,75,.06);
                    }

                    .public-results-table-scroll {
                        overflow-x: auto;
                    }

                    .public-results-table {
                        width: 100%;
                        border-collapse:
                            collapse;
                        min-width: 760px;
                    }

                    .public-results-table th {
                        background:
                            ${BRAND.navy};
                        color: white;
                        padding: 14px;
                        text-align: left;
                        font-size: 12px;
                        white-space:
                            nowrap;
                    }

                    .public-results-table td {
                        padding: 14px;
                        border-bottom:
                            1px solid
                            ${BRAND.border};
                        font-size: 13px;
                    }

                    .public-results-table tbody tr:hover {
                        background:
                            #F8FAFC;
                    }

                    .public-result-rank {
                        width: 34px;
                        height: 34px;
                        border-radius: 50%;
                        display: inline-flex;
                        align-items: center;
                        justify-content: center;
                        background:
                            #EEF3F7;
                        color:
                            ${BRAND.navy};
                        font-weight: 800;
                    }

                    .public-result-rank.top {
                        background:
                            ${BRAND.gold};
                        color:
                            ${BRAND.navy};
                    }

                    .public-results-search {
                        width: 100%;
                        border:
                            1px solid
                            ${BRAND.border};
                        border-radius: 9px;
                        padding:
                            11px 14px 11px 39px;
                        outline: none;
                        font-size: 14px;
                        box-sizing:
                            border-box;
                    }

                    .public-results-search:focus {
                        border-color:
                            ${BRAND.blue};
                        box-shadow:
                            0 0 0 3px
                            rgba(6,58,107,.08);
                    }

                    @media (max-width: 760px) {
                        .public-results-stats {
                            grid-template-columns:
                                1fr;
                        }

                        .public-results-hero {
                            padding: 22px;
                        }
                    }
                `}
            </style>

            <div className="public-results-container">
                <div className="public-results-hero">
                    <div
                        style={{
                            display: "flex",
                            alignItems:
                                "center",
                            gap: 12,
                        }}
                    >
                        <FaGraduationCap
                            style={{
                                fontSize: 32,
                                color:
                                    BRAND.gold,
                            }}
                        />

                        <div>
                            <div
                                style={{
                                    fontSize: 27,
                                    fontWeight: 800,
                                }}
                            >
                                Entrance
                                Examination
                                Results
                            </div>

                            <div
                                style={{
                                    marginTop: 5,
                                    opacity: 0.82,
                                    fontSize: 14,
                                }}
                            >
                                Official published
                                entrance examination
                                results
                            </div>
                        </div>
                    </div>

                    <div
                        style={{
                            marginTop: 18,
                            fontSize: 13,
                            opacity: 0.82,
                        }}
                    >
                        Only applicants who
                        successfully passed the
                        entrance examination are
                        listed below.
                    </div>
                </div>

                <div className="public-results-stats">
                    <div className="public-results-stat">
                        <div
                            style={{
                                display:
                                    "flex",
                                alignItems:
                                    "center",
                                gap: 9,
                                color:
                                    BRAND.blue,
                                fontWeight: 700,
                                fontSize: 13,
                            }}
                        >
                            <FaUsers />
                            TOTAL WHO TOOK
                        </div>

                        <div className="public-results-stat-number">
                            {data.total_took}
                        </div>
                    </div>

                    <div className="public-results-stat">
                        <div
                            style={{
                                display:
                                    "flex",
                                alignItems:
                                    "center",
                                gap: 9,
                                color:
                                    BRAND.green,
                                fontWeight: 700,
                                fontSize: 13,
                            }}
                        >
                            <FaCheckCircle />
                            TOTAL PASSED
                        </div>

                        <div
                            className="public-results-stat-number"
                            style={{
                                color:
                                    BRAND.green,
                            }}
                        >
                            {data.total_passed}
                        </div>
                    </div>

                    <div className="public-results-stat">
                        <div
                            style={{
                                display:
                                    "flex",
                                alignItems:
                                    "center",
                                gap: 9,
                                color:
                                    BRAND.red,
                                fontWeight: 700,
                                fontSize: 13,
                            }}
                        >
                            <FaUsers />
                            TOTAL FAILED
                        </div>

                        <div
                            className="public-results-stat-number"
                            style={{
                                color:
                                    BRAND.red,
                            }}
                        >
                            {data.total_failed}
                        </div>
                    </div>
                </div>

                <div className="public-results-table-wrap">
                    <div
                        style={{
                            padding: 16,
                            borderBottom:
                                `1px solid ${BRAND.border}`,
                            display:
                                "flex",
                            alignItems:
                                "center",
                            gap: 15,
                            justifyContent:
                                "space-between",
                            flexWrap:
                                "wrap",
                        }}
                    >
                        <div>
                            <div
                                style={{
                                    fontSize: 17,
                                    fontWeight: 800,
                                    color:
                                        BRAND.navy,
                                }}
                            >
                                Successful
                                Applicants
                            </div>

                            <div
                                style={{
                                    color:
                                        BRAND.gray,
                                    fontSize: 12,
                                    marginTop: 3,
                                }}
                            >
                                {data.total_passed}{" "}
                                applicant
                                {data.total_passed ===
                                1
                                    ? ""
                                    : "s"}{" "}
                                passed the
                                entrance
                                examination
                            </div>
                        </div>

                        <div
                            style={{
                                position:
                                    "relative",
                                width: 280,
                                maxWidth:
                                    "100%",
                            }}
                        >
                            <FaSearch
                                style={{
                                    position:
                                        "absolute",
                                    left: 13,
                                    top: 13,
                                    color:
                                        BRAND.gray,
                                }}
                            />

                            <input
                                className="public-results-search"
                                value={
                                    search
                                }
                                onChange={(
                                    event
                                ) =>
                                    setSearch(
                                        event
                                            .target
                                            .value
                                    )
                                }
                                placeholder="Search passed applicants..."
                            />
                        </div>
                    </div>

                    <div className="public-results-table-scroll">
                        <table className="public-results-table">
                            <thead>
                                <tr>
                                    <th>
                                        Rank
                                    </th>

                                    <th>
                                        Applicant
                                    </th>

                                    <th>
                                        Mathematics
                                    </th>

                                    <th>
                                        English
                                    </th>

                                    <th>
                                        Average
                                    </th>

                                    <th>
                                        Result
                                    </th>
                                </tr>
                            </thead>

                            <tbody>
                                {filteredResults.length ===
                                0 ? (
                                    <tr>
                                        <td
                                            colSpan={
                                                6
                                            }
                                            style={{
                                                textAlign:
                                                    "center",
                                                padding:
                                                    45,
                                                color:
                                                    BRAND.gray,
                                            }}
                                        >
                                            No passed
                                            applicant
                                            matches
                                            your
                                            search.
                                        </td>
                                    </tr>
                                ) : (
                                    filteredResults.map(
                                        (
                                            result,
                                            index
                                        ) => {
                                            const rank =
                                                Number(
                                                    result.rank ||
                                                        index +
                                                            1
                                                );

                                            return (
                                                <tr
                                                    key={
                                                        result.application_number ||
                                                        `${result.applicant_name}-${rank}`
                                                    }
                                                >
                                                    <td>
                                                        <span
                                                            className={`public-result-rank ${
                                                                rank <=
                                                                3
                                                                    ? "top"
                                                                    : ""
                                                            }`}
                                                        >
                                                            {rank <=
                                                            3 ? (
                                                                <FaMedal />
                                                            ) : (
                                                                rank
                                                            )}
                                                        </span>
                                                    </td>

                                                    <td>
                                                        <div
                                                            style={{
                                                                fontWeight:
                                                                    800,
                                                                color:
                                                                    BRAND.navy,
                                                            }}
                                                        >
                                                            {
                                                                result.applicant_name
                                                            }
                                                        </div>
                                                    </td>

                                                    <td>
                                                        <strong>
                                                            {Number(
                                                                result.mathematics_score ||
                                                                    0
                                                            ).toFixed(
                                                                2
                                                            )}
                                                        </strong>
                                                        /
                                                        {Number(
                                                            result.mathematics_total ||
                                                                70
                                                        ).toFixed(
                                                            0
                                                        )}
                                                    </td>

                                                    <td>
                                                        <strong>
                                                            {Number(
                                                                result.english_score ||
                                                                    0
                                                            ).toFixed(
                                                                2
                                                            )}
                                                        </strong>
                                                        /
                                                        {Number(
                                                            result.english_total ||
                                                                70
                                                        ).toFixed(
                                                            0
                                                        )}
                                                    </td>

                                                    <td>
                                                        <strong
                                                            style={{
                                                                fontSize:
                                                                    17,
                                                                color:
                                                                    BRAND.navy,
                                                            }}
                                                        >
                                                            {Number(
                                                                result.average ||
                                                                    0
                                                            ).toFixed(
                                                                2
                                                            )}
                                                        </strong>
                                                        /
                                                        70
                                                    </td>

                                                    <td>
                                                        <span
                                                            style={{
                                                                display:
                                                                    "inline-flex",
                                                                alignItems:
                                                                    "center",
                                                                gap: 6,
                                                                color:
                                                                    BRAND.green,
                                                                fontWeight:
                                                                    800,
                                                            }}
                                                        >
                                                            <FaCheckCircle />
                                                            PASSED
                                                        </span>
                                                    </td>
                                                </tr>
                                            );
                                        }
                                    )
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div
                    style={{
                        textAlign:
                            "center",
                        marginTop: 18,
                        color:
                            BRAND.gray,
                        fontSize: 12,
                    }}
                >
                    <FaTrophy
                        style={{
                            color:
                                BRAND.gold,
                            marginRight: 6,
                        }}
                    />
                    Entrance examination
                    results are official
                    published results.
                </div>
            </div>
        </div>
    );
};

export default EntrancePublicResults;